import { PatientsTagsEnum } from './types'

//   DOCTOR_APPOINTMENT_REQUEST_SENT = "Doctor Appointment Request Sent",
//   REPORT_SENT = "Report Sent",
// export const getTagColor = (tag?: string) => {
//   if (!tag) return
//
//   switch (tag) {
//     case PatientsTagsEnum.COMPLICATIONS_VISUELLES: {
//       return '#ea6f0b'
//     }
//     case PatientsTagsEnum.MACULOPATHIE_DIABETIQUE: {
//       return '#ea0b5c'
//     }
//     case PatientsTagsEnum.RETINOPATHIE_DIABETIQUE: {
//       return '#9a049f'
//     }
//
//     default: {
//       return tag ? `#${(tag.split('').reduce((v, c) => 419 * (v + c.charCodeAt(0) * 2557), 0) % (256 * 256 * 256)).toString(16)}` : '#000000'
//     }
//   }
// }

// Fixed, hand-picked colors
const FIXED_COLORS: Record<PatientsTagsEnum, `#${string}`> = {
  [PatientsTagsEnum.COMPLICATIONS_VISUELLES]: '#ea6f0b',
  [PatientsTagsEnum.MACULOPATHIE_DIABETIQUE]: '#ea0b5c',
  [PatientsTagsEnum.RETINOPATHIE_DIABETIQUE]: '#9a049f',
}

// For collision checks
const USED_COLORS = new Set(Object.values(FIXED_COLORS))

// --- utils ---
const padHex = (n: number) => n.toString(16).padStart(2, '0')

const hslToHex = (h: number, s: number, l: number): `#${string}` => {
  // h [0,360), s/l [0,100]
  const S = s / 100
  const L = l / 100
  const C = (1 - Math.abs(2 * L - 1)) * S
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = L - C / 2

  let r = 0,
    g = 0,
    b = 0
  if (h < 60) [r, g, b] = [C, X, 0]
  else if (h < 120) [r, g, b] = [X, C, 0]
  else if (h < 180) [r, g, b] = [0, C, X]
  else if (h < 240) [r, g, b] = [0, X, C]
  else if (h < 300) [r, g, b] = [X, 0, C]
  else [r, g, b] = [C, 0, X]

  const R = Math.round((r + m) * 255)
  const G = Math.round((g + m) * 255)
  const B = Math.round((b + m) * 255)
  return `#${padHex(R)}${padHex(G)}${padHex(B)}`
}

const hexToRgb = (hex: `#${string}`) => {
  const h = hex.replace('#', '')
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

// Perceptual-ish distance (weighted RGB)
const colorDistance = (a: `#${string}`, b: `#${string}`) => {
  const A = hexToRgb(a)
  const B = hexToRgb(b)
  const rMean = (A.r + B.r) / 2
  const dR = A.r - B.r
  const dG = A.g - B.g
  const dB = A.b - B.b
  return Math.sqrt((2 + rMean / 256) * dR * dR + 4 * dG * dG + (2 + (255 - rMean) / 256) * dB * dB)
}

// WCAG relative luminance
const relLuminance = (hex: `#${string}`) => {
  const { r, g, b } = hexToRgb(hex)
  const toLin = (v: number) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const R = toLin(r),
    G = toLin(g),
    B = toLin(b)
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

// Deterministic string hash → [0..360)
const hueFromString = (str: string) => {
  let h = 2166136261 // FNV-ish start
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = (h * 16777619) >>> 0
  }
  return h % 360
}

// --- main ---
export const getTagColor = (tag?: string): `#${string}` | undefined => {
  if (!tag) return

  // 1) fixed picks for known tags
  if ((Object.keys(FIXED_COLORS) as PatientsTagsEnum[]).includes(tag as PatientsTagsEnum)) {
    return FIXED_COLORS[tag as PatientsTagsEnum]
  }

  // 2) generate deterministic HSL for others
  // UX constraints:
  // - Not light: keep L in [35..50]
  // - Saturation reasonably strong for readability
  let hue = hueFromString(tag)
  const saturation = 68
  let lightness = 42

  // 3) produce a candidate and ensure it's not too light
  let candidate = hslToHex(hue, saturation, lightness)
  if (relLuminance(candidate) > 0.6) {
    lightness = 38
    candidate = hslToHex(hue, saturation, lightness)
  }

  // 4) avoid collisions / near-duplicates with existing colors
  // If too close, rotate hue by a coprime step until distinct.
  const MIN_DISTANCE = 80 // tweak as needed (60..100 is a good band)
  const HUE_STEP = 37 // coprime to 360 → better spread
  let tries = 0
  const used = Array.from(USED_COLORS)

  while (tries < 12 && used.some((u) => colorDistance(candidate, u as `#${string}`) < MIN_DISTANCE)) {
    hue = (hue + HUE_STEP) % 360
    candidate = hslToHex(hue, saturation, lightness)
    tries++
  }

  return candidate
}
